import type { ReactNode } from 'react';
import type { OperationDto } from '@/shared/types';
import { Skeleton } from '@/shared/components/ui';
import { formatReceivedAmount } from '@/shared/lib';
import { cn } from '@/shared/utils';

type Props = {
  operation: OperationDto;
  typeName?: string;
  iconAssetCodes?: string[];
  assetsIcons?: Record<string, string>;
  typeIcon?: string;
  isIconLoading?: boolean;
  actions?: ReactNode;
  amountHint?: ReactNode;
};

const formatSignedAmount = (
  direction: OperationDto['direction'],
  amount: number,
  code: string
) =>
  `${direction === 'credit' ? '+' : '-'} ${formatReceivedAmount(amount)} ${code}`;

const getAssetIcons = (
  codes: string[] | undefined,
  assetsIcons: Record<string, string> | undefined
) => {
  if (!codes?.length) return [];

  const icons = codes.map(code => ({ code, icon: assetsIcons?.[code] }));

  return icons.every(({ icon }) => icon)
    ? (icons as Array<{ code: string; icon: string }>)
    : [];
};

const HeaderIcons = ({
  assetIcons,
  typeIcon,
  isLoading,
}: {
  assetIcons: Array<{ code: string; icon: string }>;
  typeIcon?: string;
  isLoading?: boolean;
}) => {
  if (isLoading) {
    return <Skeleton variant="avatar" className="size-[5.2rem]" />;
  }

  if (assetIcons.length > 0) {
    return assetIcons.map(({ code, icon }, index) => (
      <img
        key={`${code}-${index}`}
        src={icon}
        alt={code}
        className={cn(
          'size-[5.2rem] shrink-0 rounded-full object-cover',
          index < assetIcons.length - 1 && '-mr-[1.95rem]'
        )}
      />
    ));
  }

  if (!typeIcon) return null;

  return (
    <span className="flex size-[5.2rem] shrink-0 items-center justify-center rounded-full bg-secondary-bg">
      <img
        src={typeIcon}
        alt=""
        className="size-[2.8rem] dark:brightness-0 dark:invert"
      />
    </span>
  );
};

export const OperationDetailsHeader = ({
  operation,
  typeName,
  iconAssetCodes,
  assetsIcons,
  typeIcon,
  isIconLoading,
  actions,
  amountHint,
}: Props) => {
  const { asset, additionalAsset, additionalAmount, additionalDirection } =
    operation;

  const assetIcons = isIconLoading
    ? []
    : getAssetIcons(iconAssetCodes, assetsIcons);
  const blurIcons = assetIcons.map(({ icon }) => icon);

  const isCredit = operation.direction === 'credit';

  return (
    <div className="relative flex flex-col gap-[1.6rem] pb-[2.4rem]">
      {blurIcons.length > 0 && (
        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute -top-[3.8rem] flex opacity-[0.24]',
            blurIcons.length > 1
              ? '-left-[8.6rem] blur-[3.6rem]'
              : '-left-[3.8rem] blur-[5rem]'
          )}>
          {blurIcons.map((icon, index) => (
            <div
              key={`${icon}-${index}`}
              className="size-[12.8rem] shrink-0 overflow-hidden rounded-full">
              <img
                src={icon}
                alt=""
                className="size-full scale-[1.6] object-cover"
              />
            </div>
          ))}
        </div>
      )}

      <div className="relative flex">
        <HeaderIcons
          assetIcons={assetIcons}
          typeIcon={typeIcon}
          isLoading={isIconLoading}
        />
      </div>

      <div className="relative flex flex-col gap-[0.4rem]">
        {typeName && <p className="medium5 text-primary">{typeName}</p>}

        <div className="flex items-center gap-[0.8rem]">
          <p
            className={cn(
              'headline3 min-w-0 wrap-anywhere',
              isCredit ? 'text-primary-cyan' : 'text-primary'
            )}>
            {formatSignedAmount(
              operation.direction,
              operation.amount,
              asset.code
            )}
          </p>
          {amountHint}
        </div>

        {additionalAsset && additionalDirection && additionalAmount ? (
          <p className="medium5 text-primary wrap-anywhere">
            {formatSignedAmount(
              additionalDirection,
              additionalAmount,
              additionalAsset.code
            )}
          </p>
        ) : null}
      </div>

      {actions && (
        <div className="relative flex flex-wrap items-center gap-[0.8rem]">
          {actions}
        </div>
      )}
    </div>
  );
};
